package com.evoapps.evoshift.push

import com.evoapps.evoshift.domain.provider.DeviceInfoProvider
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.auth.RegisterDeviceTokenUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import com.evoapps.evoshift.util.AppLocale
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch

/**
 * Keeps this device's entry in users/{uid}/devices current whenever the app starts signed in
 * (SH-109): the server writes pushes in the language saved there (functions/pushText.js), and the
 * app's language can change between sign-ins (Android's per-app language recreates the app's
 * screens, which starts this again; the in-app language picker of SH-137 changes it while the
 * app runs, so a new pick saves it again). Signing in (AuthViewModel), a new token
 * (EVoShiftMessagingService, IosPush) save it too; this covers the launches in between. Platforms
 * without a push token (web, desktop) save nothing.
 */
class DeviceTokenSync(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val registerDeviceTokenUseCase: RegisterDeviceTokenUseCase,
    private val deviceInfo: DeviceInfoProvider,
    /** The language picked in the app (SH-137); each change saves the device again. */
    private val pickedLanguage: Flow<String?> = AppLocale.chosen
) {
    fun start(scope: CoroutineScope): Job = scope.launch {
        val uids = getCurrentUserUseCase().map { it?.uid }.distinctUntilChanged()
        combine(uids, pickedLanguage) { uid, _ -> uid }.collect { uid ->
            if (uid == null) return@collect
            val token = runCatching { deviceInfo.getFcmToken() }.getOrNull() ?: return@collect
            registerDeviceTokenUseCase(
                userId = uid,
                token = token,
                platform = deviceInfo.getPlatform(),
                deviceName = deviceInfo.getDeviceName(),
                language = deviceInfo.getLanguage()
            )
        }
    }
}
